import api, { createApiWithNewBasePath, createApiWithNoBasePath } from "@api";

/**
 * The calls behind the task creation flow's email login step (RT-4321).
 *
 * All answer a logged-out visitor, so none goes through `/web/api/v1`.
 * `services/sessions.ts` is not reused: it posts to a route nothing answers.
 */

export interface IUserAvailability {
  first_name: string;
}

/**
 * Whether an address has an account, and the name to greet it with. A 404 is
 * its "no account" answer rather than a failure, so callers branch on it.
 */
export const getUserAvailability = async (
  email: string
): Promise<IUserAvailability> => {
  const { data } = await createApiWithNewBasePath.get<
    { email: string },
    IUserAvailability
  >("public/users/availability", { email });

  return data;
};

/** True for the rejection above that means "this address has no account". */
export const isUserNotFound = (error: unknown): boolean =>
  api.isApiError(error) && error.response?.status === 404;

export interface ISessionUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  locale: string;
  pro_client: boolean;
}

export interface IUserSession {
  user: ISessionUser;
  token: string;
}

export interface ICreateUserSessionInput {
  email: string;
  password: string;
}

/**
 * `account_locked` arrives on two statuses, so callers key on the code.
 *
 * `unauthorized` covers a wrong password *and* an email with no account: the
 * endpoint answers them identically so it never confirms which emails exist
 * (`API::Public::UserSessionsController#reject_invalid_credentials!`).
 */
export type TUserSessionError =
  | "email_missing"
  | "password_missing"
  | "unauthorized"
  | "account_locked"
  | "email_banned"
  | "account_banned"
  | "user_not_found";

/**
 * Every instance, not just the default one: the call that has to be
 * authenticated next — the draft's claim POST — goes through
 * `createApiWithNewBasePath`.
 */
export const storeAuthToken = (token: string) => {
  api.setAuthToken(token);
  createApiWithNewBasePath.setAuthToken(token);
  createApiWithNoBasePath.setAuthToken(token);

  try {
    localStorage.setItem("Authorization", token);
  } catch {
    // Safari private mode: only surviving a reload is lost.
  }
};

/** Also signs the visitor into the Rails session, for the pages it hands to. */
export const createUserSession = async (
  input: ICreateUserSessionInput
): Promise<IUserSession> => {
  const { data } = await createApiWithNewBasePath.post<
    ICreateUserSessionInput,
    IUserSession
  >("public/users/sessions", input);

  storeAuthToken(data.token);

  return data;
};

/** The code the endpoint refused with, or `undefined` for one it did not name. */
export const getRefusalCode = <T extends string>(error: unknown): T | undefined => {
  if (!api.isApiError(error)) return undefined;

  const data: { error?: T } | undefined = error.response?.data;

  return data?.error;
};

export const getUserSessionError = getRefusalCode<TUserSessionError>;

export interface IRegisterUserInput {
  firstname: string;
  lastname: string;
  email: string;
  password: string;
  /** E.164. Optional for the visitor, and omitted rather than sent empty. */
  phone_number?: string;
  pro_client: boolean;
  consent_email: boolean;
  /** ISO 3166-1 alpha-2. Absent falls back to the request's own country. */
  country_code?: string;
  locale?: string;
  /** Attribution source (`getKnowFrom`); absent when the visitor came in untracked. */
  know_from?: string;
}

/** Every refusal `Users::RegisterUser` names, plus the model-level ones. */
export type TUserRegistrationError =
  | "firstname_missing"
  | "lastname_missing"
  | "email_missing"
  | "email_invalid"
  | "email_taken"
  | "email_banned"
  | "password_missing"
  | "password_invalid"
  | "phone_number_invalid"
  | "forbidden_name"
  | "country_not_found"
  | "registration_failed";

/**
 * Creates the account and signs it in, in one call — the response carries the
 * same `{ user, token }` a login does, so the draft's claim POST that follows
 * is authenticated the same way.
 */
export const registerUser = async (
  input: IRegisterUserInput
): Promise<IUserSession> => {
  const { data } = await createApiWithNewBasePath.post<
    IRegisterUserInput,
    IUserSession
  >("public/users", input);

  storeAuthToken(data.token);

  return data;
};

export const getUserRegistrationError = getRefusalCode<TUserRegistrationError>;

export interface IEmailSuggestionResponse {
  error_found: boolean;
  new_email: string;
}

/**
 * A likelier spelling of a mistyped address ("gmial.com" → "gmail.com").
 *
 * The legacy Rails action, already reachable for a guest: its only gate is
 * `request.xhr?`, hence the header.
 *
 * TODO(RT-4321): move to `/api/public` when the endpoint doc is revisited.
 */
export const getEmailSuggestion = async (
  email: string
): Promise<string | undefined> => {
  const { data } = await createApiWithNoBasePath.post<
    { email_to_check: string },
    IEmailSuggestionResponse
  >(
    "/check_email",
    { email_to_check: email },
    { headers: { "X-Requested-With": "XMLHttpRequest" } }
  );

  // Some paths report a correction equal to the input.
  if (!data.error_found || !data.new_email || data.new_email === email)
    return undefined;

  return data.new_email;
};
